import { useEffect, useState } from "react";
import { ClientFormFields } from "@/components/ClientFormFields";
import { ColorSettings } from "@/components/ColorSettings";
import {
  EMPTY_CLIENT_FORM,
  clientToFormValues,
  toClientProfileInput,
  validateClientForm,
  type ClientFormErrors,
  type ClientFormValues,
} from "@/components/client-form";
import { usePlans, useUpdateClient } from "@/hooks/useClients";
import { useCreateClientForOnboarding } from "@/hooks/useOnboarding";
import { useSettingsMutation, useSettingsQuery } from "@/hooks/useSettings";
import { WizardFooter } from "./WizardFooter";
import { ApiError, type SuperAdminClient } from "@/lib/api";

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

type Props = {
  /** null → the client does not exist yet and this step creates it. */
  client: SuperAdminClient | null;
  onDone: () => void;
  onCreated: (client: SuperAdminClient) => void;
  onDirtyChange: (dirty: boolean) => void;
};

/**
 * Step 1 — the client's profile, plus the two widget presentation settings that carry
 * no dependency on a deployment.
 *
 * It both creates and edits, so the data is captured in exactly one place. The slug is
 * only editable here on create (it is immutable afterwards).
 *
 * Editing writes twice: the profile through `PATCH /super-admin/clients/:id` and the
 * colour + Maps URL through `PUT /crm/settings`. That endpoint replaces every one of
 * its keys, so `initialOptions` and `greeting` are carried over from the current values
 * rather than sent as null — otherwise revisiting this step would wipe the chips and the
 * opening message configured from the Agente page. Both are that page's business
 * (initial options can only be AI-suggested once a deployment exists).
 */
export function StepClient({
  client,
  onDone,
  onCreated,
  onDirtyChange,
}: Props): React.JSX.Element {
  const isCreate = client === null;
  const { data: plans } = usePlans();
  const updateClient = useUpdateClient();
  const createClient = useCreateClientForOnboarding();
  const { data: settings, isPending: settingsLoading } = useSettingsQuery();
  const saveSettings = useSettingsMutation();

  const [values, setValues] = useState<ClientFormValues>(() =>
    client ? clientToFormValues(client) : EMPTY_CLIENT_FORM,
  );
  const [errors, setErrors] = useState<ClientFormErrors>({});
  const [primaryColor, setPrimaryColor] = useState("");
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [settingsHydrated, setSettingsHydrated] = useState(false);

  // Hydrate once, so a slow settings response cannot clobber typing in progress. On
  // create there is nothing to hydrate from — the client does not exist yet.
  useEffect(() => {
    if (isCreate || !settings || settingsHydrated) return;
    setPrimaryColor(settings.primaryColor ?? "");
    setGoogleMapsUrl(settings.googleMapsUrl ?? "");
    setSettingsHydrated(true);
  }, [isCreate, settings, settingsHydrated]);

  const colorInvalid = primaryColor.trim() !== "" && !HEX_COLOR_RE.test(primaryColor.trim());
  const mapsInvalid = googleMapsUrl.trim() !== "" && !isValidHttpUrl(googleMapsUrl.trim());
  const isBusy = updateClient.isPending || saveSettings.isPending || createClient.isPending;

  async function handleContinue(): Promise<void> {
    const found = validateClientForm(values, { isEdit: !isCreate });
    setErrors(found);
    if (Object.keys(found).length > 0 || colorInvalid || mapsInvalid) return;

    const profile = toClientProfileInput(values);
    const presentation = {
      googleMapsUrl: googleMapsUrl.trim() || null,
      primaryColor: primaryColor.trim() ? primaryColor.trim().toLowerCase() : null,
    };

    try {
      if (isCreate) {
        const created = await createClient.mutateAsync({
          ...profile,
          slug: values.slug,
          settings: presentation,
        });
        onCreated(created);
        return;
      }

      await updateClient.mutateAsync({ id: client.id, ...profile });
      await saveSettings.mutateAsync({
        initialOptions: settings?.initialOptions ?? null,
        greeting: settings?.greeting ?? null,
        ...presentation,
      });
      onDone();
    } catch (err) {
      // A taken slug is the one failure the operator can act on, and it belongs on the
      // field rather than in a toast that leaves the form looking valid. It usually
      // means the client already exists, so say where to pick it up.
      if (err instanceof ApiError && err.status === 409) {
        setErrors({
          slug: "Ese slug ya está en uso. Si el cliente ya existe, continúa su onboarding desde la lista de Clientes.",
        });
        return;
      }
      // Everything else already toasted from the mutation; swallowing keeps the step on
      // screen with the operator's input intact.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-ink-500">
        {isCreate
          ? "El slug identifica al cliente en la plataforma y no se puede cambiar después. La marca decide qué tipos de cliente hay; el tipo define la plantilla de la base de conocimiento."
          : "La marca decide qué tipos de cliente hay; el tipo define la plantilla de la base de conocimiento. Los orígenes son los dominios donde vivirá el widget."}
      </p>

      <ClientFormFields
        values={values}
        onChange={(next) => {
          setValues(next);
          onDirtyChange(true);
          if (Object.keys(errors).length > 0) setErrors({});
        }}
        errors={errors}
        plans={plans ?? []}
        disabled={isBusy}
        isEdit={!isCreate}
        // Location, not widget config: the agent shares this link on WhatsApp too.
        locationExtras={
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700">
              Ubicación en Google Maps <span className="text-ink-400">(opcional)</span>
            </label>
            <input
              type="url"
              value={googleMapsUrl}
              onChange={(e) => {
                setGoogleMapsUrl(e.target.value);
                onDirtyChange(true);
              }}
              disabled={isBusy || (!isCreate && settingsLoading)}
              placeholder="https://maps.app.goo.gl/…"
              className={`rounded-md border bg-white px-2 py-1.5 text-sm text-ink-900 disabled:opacity-50 ${
                mapsInvalid ? "border-red-400" : "border-ink-200"
              }`}
            />
            <span className="text-xs text-ink-400">
              El agente la comparte cuando alguien pregunta cómo llegar.
            </span>
            {mapsInvalid && (
              <span className="text-xs text-red-500">
                Debe ser una URL http(s) válida
              </span>
            )}
          </div>
        }
        widgetExtras={
          <ColorSettings
            value={primaryColor}
            onChange={(value) => {
              setPrimaryColor(value);
              onDirtyChange(true);
            }}
            invalid={colorInvalid}
          />
        }
        // An empty colour means "use the ScholaOps default", so only a set one counts
        // as configuration worth opening the section for.
        widgetExtrasConfigured={primaryColor.trim() !== ""}
      />

      <WizardFooter
        onBack={null}
        onContinue={() => void handleContinue()}
        canContinue={!colorInvalid && !mapsInvalid}
        isBusy={isBusy}
        continueLabel={isCreate ? "Crear y continuar" : "Continuar"}
      />
    </div>
  );
}
